/**
 * ProtectedRoute — Redirects unauthenticated users to /login.
 *
 * Preserves the original path in ?redirect= so they return to it after login.
 * Shows a loading spinner while auth state is initializing.
 */

import React from "react";
import { Navigate, useLocation } from "react-router";
import { Loader2 } from "lucide-react";
import { useAuthContext } from "../../../contexts/AuthContext";

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthContext();
  const location = useLocation();

  // Still checking session — show a centered spinner
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-fp-emerald-500" />
      </div>
    );
  }

  // Not authenticated — redirect to login with return path
  if (!isAuthenticated) {
    const redirectPath = location.pathname + location.search;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirectPath)}`}
        replace
      />
    );
  }

  return <>{children}</>;
}
